import devConfigJSON from '../data/dev_config.json'
import prodConfigJSON from '../data/prod_config.json'

import moment from 'moment'
import momentDurationFormatSetup from 'moment-duration-format'
import { storage, REFRESH_TOKEN_TYPE_CASUAL, REFRESH_TOKEN_TYPE_REGISTERED } from './Storage.js'
import { SHARE_TYPE } from './shareType'

import { Spinnaker } from 'keel-spin-module/lib/keel-spin-module-es2015'
import { SharedFunctions } from './SharedFunctions.js'

const ERRORS = Object.freeze({
  PEEK: 'Error while loading shared content for token.',
  REDEEM: 'Error while redeeming shared content.',
  PROGRAMMEDETAILS: 'Error while loading programme details.',
  VODPLAYBACKDETAILS: 'Error while loading VOD playback details.'
})

const PROD_URL = 'https://aftv.mobi'
const DEV_URL = 'https://aftv.ec-dev.spinnaker.tv'
const LOCAL_URL = 'http://localhost'

const loadedData = {
  description: '',
  sharerName: '',
  expires: -1,
  manifestUrl: '',
  posterSmall: '',
  posterBig: '',
  backdropSmall: '',
  backdropBig: '',
  duration: -1,
  releaseYear: -1,
  rating: '',
  title: '',
  redemptionsLeft: 0,
  sharingToken: '',
  // this will trigger immediate playout
  playoutOnInit: false
}

let spinnaker
const lang = 'en'
let envSpin = PROD_URL

class SpinnakerApi {
  constructor () {
    let env = prodConfigJSON
    if (!this.isProd()) {
      env = devConfigJSON
      envSpin = DEV_URL
    }
    if (!spinnaker) {
      spinnaker = new Spinnaker(envSpin, navigator.userAgent + ' aftv-user')
    }
    this.spinnaker = spinnaker
    this.grecaptchaSiteKey = env.grecaptchaSiteKey
    this.rootCategoryId = env.rootCategoryId
    this.menuCategoryId = env.menuCategoryId
    this.appear2meeId = env.appear2meeId
  }

  isProd () {
    return window.location.origin.indexOf(DEV_URL) !== 0 &&
      window.location.origin.indexOf(LOCAL_URL) !== 0
  }

  async getSplashScreen () {
    const splashScreen = await spinnaker.getConfig().getClientConfiguration()
    return splashScreen.data.config
  }

  async getCompliance () {
    return spinnaker.getConfig().getCompliance()
  }

  async recordTermsAndConditionsAcceptance (version) {
    return spinnaker.getAccount().recordComplianceOptions('termsAndConditions', version, {})
  }

  async recordDataConsentChoices (version, options) {
    return spinnaker.getAccount().recordComplianceOptions('consent', version, options)
  }

  /**
   * Fetch all shared content data.
   */
  fetchData (sharingToken) {
    return this.getPeekDataForToken(sharingToken)
      .then((response) => {
        console.info('[2] peek.data', response.data)
        Object.assign(loadedData, response.data)
        const { sharerName, expires, redemptionsLeft, programmeId, shareType, sharingUrl, image, description, title } = response.data
        loadedData.sharingUrl = sharingUrl
        loadedData.image = image
        loadedData.description = this.decodeHtml(description)
        loadedData.title = this.decodeHtml(title)
        loadedData.sharerName = sharerName
        loadedData.isShare = true
        // this specifies if given item is Gift, Recommend, Sell or Share
        loadedData.shareType = shareType
        let shareText = ''
        let playoutStr = 'Watch'
        let msg = ''
        switch (shareType) {
          default:
          case SHARE_TYPE.SHARE:
            shareText = 'shared this with you'
            loadedData.purchased = true
            loadedData.useRedeem = true
            loadedData.canWatch = loadedData.playout ? loadedData.playout.state !== 'Stopped' : true
            if (loadedData.canWatch === false || loadedData.redemptionsLeft <= 0) {
              loadedData.playoutOnInit = false
            }
            break
          case SHARE_TYPE.GIFT:
            shareText = 'gifted this to you'
            loadedData.purchased = true
            loadedData.useRedeem = true
            break
          case SHARE_TYPE.RECOMMEND:
          case SHARE_TYPE.SELL:
            shareText = 'recommended this to you'
            loadedData.isSell = true
            break
          case SHARE_TYPE.WATCH_WITH_ME:
            shareText = 'wants to watch this with you'
            loadedData.useRedeem = true
            break
        }
        const noSharerMsg = 'Please find this shared video'
        msg = sharerName ? `${sharerName} ${shareText}` : noSharerMsg
        loadedData.sharerMsg = msg
        loadedData.expires = expires
        loadedData.redemptionsLeft = redemptionsLeft
        loadedData.playoutStr = playoutStr
        return this.getProgrammeDetails(programmeId)
      })
      .then((response) => {
        console.info('[3] programmeDetailsResponse.data', response.data)
        Object.assign(loadedData, this.mapItem(response.data))
        return Promise.resolve(loadedData)
      })
      .catch(() => {
        return Promise.reject(ERRORS.VODPLAYBACKDETAILS)
      })
  }

  preloadImage (src) {
    let imageSrc = src
    if (imageSrc) {
      imageSrc = this.replaceToHttps(src)
      const res = document.createElement('link')
      res.rel = 'preload'
      res.as = 'image'
      res.href = imageSrc
      document.head.appendChild(res)
    }
    return imageSrc
  }

  mapItem (item, expires) {
    let loadedData = {}
    const {
      id, images, duration, flags, releaseYear, title, name, progId, contentRights,
      availTo, desc, seriesNum, episodeNum, adType, ads, typeName, jsonValue, type, categoryId, parentCategoryIdsByType
    } = item

    switch (type) {
      case 'PROMO':
        loadedData.isPromo = true
        loadedData.id = categoryId
        break
      case 'CONTENT_AREA':
        loadedData.isCategory = true
        loadedData.isMain = (item.metadata.MAIN_SWIMLANE === 'true')
        loadedData.isYouTube = (item.metadata.YOUTUBE_SWIMLANE === 'true')
        loadedData.id = categoryId
        break
      case 'SWIMLANE_GENERAL':
        loadedData.isSwimlane = true
        loadedData.id = categoryId
        break
      default:
        loadedData.id = id
        switch (typeName) {
          case 'poll':
            loadedData.isPoll = true
            loadedData.pollMetadata = jsonValue
            break
          case 'team-selector':
            loadedData.isTeamSelector = true
            loadedData.teamSelectorMetadata = jsonValue
            break
          case 'banner':
            loadedData.isBanner = true
            loadedData.bannerLink = jsonValue.externalUrl.value
            loadedData.bannerImage = this.preloadImage(jsonValue.images.banner)
            break
          case 'article':
            loadedData.isArticle = true
            loadedData.articleMetadata = jsonValue
            if (jsonValue.contents.pubDate) {
              loadedData.articleMetadata.contents.pubDateFormatted = new moment(
                jsonValue.contents.pubDate
              ).format('MMM Do YYYY, h:mm a')
            }
            break
          default:
            loadedData.isVod = true
            if (item.metadata && item.metadata.YOUTUBE_LIVE && item.metadata.YOUTUBE_LIVE_SCHEDULED_START_TIME) {
              loadedData.isLive = true
              loadedData.liveInfo = item.metadata
            }
        }
    }

    if (parentCategoryIdsByType) {
      loadedData.parentCategoryIds = Object.values(parentCategoryIdsByType)
        .filter(value => Array.isArray(value))
        .reduce((previousValue, currentValue) => {
          return [...previousValue, ...currentValue]
        }, [])
    }

    loadedData.isBrand = false
    loadedData.desc = this.decodeHtml(desc)
    loadedData.backdropSmall = this.preloadImage(images.backdropSmall)
    loadedData.backdropBig = this.preloadImage(images.backdropBig)
    loadedData.promoHeroImage = this.preloadImage(images.promo_hero_image)
    loadedData.promo = this.preloadImage(images.promo)
    loadedData.posterSmall = this.preloadImage(images.posterSmall)
    loadedData.duration = duration
    loadedData.flags = flags
    loadedData.publishedAt = item.metadata && item.metadata.YOUTUBE_PUBLISHED_AT ? item.metadata.YOUTUBE_PUBLISHED_AT : null
    loadedData.releaseYear = item.metadata && item.metadata.YOUTUBE_CHANNEL_TITLE ? 'By ' + item.metadata.YOUTUBE_CHANNEL_TITLE : releaseYear
    loadedData.name = this.decodeHtml(name)
    loadedData.title = this.decodeHtml(title)
    loadedData.progId = progId
    loadedData.adType = adType
    loadedData.ads = ads
    loadedData.youtubeLink = item.links && item.links.YOUTUBE_VIDEO ? item.links.YOUTUBE_VIDEO : null
    if (loadedData.youtubeLink) {
      loadedData.purchased = true
    }
    // in case MOVIE_HD_HLS_DRMFREE or MOVIE_SD_HLS_DRMFREE is set use this one instead of youtube link
    if (item.links && item.links.MOVIE_HD_HLS_DRMFREE) {
      loadedData.manifestUrl = item.links.MOVIE_HD_HLS_DRMFREE
      loadedData.youtubeLink = null
    } else if (item.links && item.links.MOVIE_SD_HLS_DRMFREE) {
      loadedData.manifestUrl = item.links.MOVIE_SD_HLS_DRMFREE
      loadedData.youtubeLink = null
    }
    if (item.links && item.links.EXTERNAL_LINK) {
      loadedData.externalLink = item.links.EXTERNAL_LINK
      loadedData.isPlaceholder = true
    }
    loadedData.rating = item.ratings && item.ratings.MPAA ? item.ratings.MPAA : ''
    loadedData.contentRights = contentRights

    let expiresValue = expires || availTo
    const expiresDate = new moment(expiresValue * 1000).format('MMMM Do YYYY')
    loadedData.formattedDateText = `Valid until ${expiresDate}`
    momentDurationFormatSetup(moment)
    if (loadedData.publishedAt) {
      loadedData.durationFormatted = new moment(
        loadedData.publishedAt
      ).format('MMM Do YYYY, h:mm a')
    } else {
      loadedData.durationFormatted = new moment.duration(
        duration,
        'seconds'
      ).format('h [hrs] m [min]')
    }
    loadedData.seriesNum = seriesNum
    loadedData.episodeNum = episodeNum
    return loadedData
  }

  redeem (sharingToken) {
    console.info('getManifestUrl: ' + sharingToken)
    return this.postRedeemDataForToken(sharingToken)
      .then((response) => {
        if (response.data.accessToken) {
          return this.getManifestUrl(response.data.accessToken)
        } else {
          return Promise.reject(new Error('An error occured, please try later!'))
        }
      })
  }

  getManifestUrl (accessToken) {
    let details = null
    return this.getVodPlaybackDetailsForAccessToken(accessToken)
      .then((vodPlaybackDetailsResponse) => {
        return Promise.resolve(vodPlaybackDetailsResponse.data)
      })
      // .then(() => {
      //   return this.getEventIfOnBookmarks(details.programmeId)
      // })
      // .then((response) => {
      //   details.lastPosition = response.data.lastPosition
      //   return Promise.resolve(details)
      // })
      .catch(() => {
        return Promise.resolve(details)
      })
  }

  /**
   * Checks if event is on the bookmark list
   * @param {Object} event
   */
  getEventIfOnBookmarks (progId) {
    return spinnaker.getMyContent()
      .getBookmark(progId)
  }

  replaceToHttps (url) {
    if (url) {
      if (url.indexOf('https') === -1) {
        return url.replace('http', 'https')
      } else {
        return url
      }
    } else {
      return ''
    }
  }

  decodeHtml (string) {
    if (string) {
      return string
        .replace('&amp;', '&')
        .replace('&#39;', '\'')
        .replace('&gt;', '>')
        .replace('&lt;', '<')
        .replace('&quot;', '"')
    }
  }

  /**
   * Returns peek data for sharing token.
   * @param sharingToken
   */
  getPeekDataForToken (sharingToken) {
    return spinnaker.getContentSharing().peek(sharingToken)
  }

  /**
   * Returns redeem data for sharing token.
   * @param sharingToken
   */
  postRedeemDataForToken (sharingToken) {
    return spinnaker.getContentSharing().redeem(sharingToken)
  }

  getProgrammeDetailsWithMapping (programmeId) {
    return this.getProgrammeDetails(programmeId)
      .then((response) => {
        const mappedItem = this.mapItem(response.data)
        return Promise.resolve(mappedItem)
      })
      .catch((response) => {
        return Promise.resolve(null)
      })
  }

  getProgrammeDetailsByLinkWithMapping (programmeId) {
    return this.getProgrammeDetailsByLink(programmeId)
      .then((response) => {
        const mappedItem = this.mapItem(response.data)
        return Promise.resolve(mappedItem)
      })
      .catch((response) => {
        return Promise.resolve(null)
      })
  }

  /**
   * Returns programme's details.
   * @param programmeId
   */
  getProgrammeDetails (programmeId) {
    return spinnaker.getVod().programme(lang, programmeId)
  }

  getProgrammeDetailsByLink (link) {
    return spinnaker.getVod().getUsingLink(link)
  }

  /**
   * Returns categories available in menu. Some of them will be just external links
   * that should be opened in new window.
   */
  async getMenuCategories () {
    const menuCategoryItems = await this.getAllCategoryItems(this.menuCategoryId)
    return menuCategoryItems.items
  }

  /**
   * Returns categories that can be displayed individually.
   *
   * From those that should have dedicated tab item we have to find categories that will be handled
   * in a special form:
   * - <ff>MAIN_SWIMLANE === 'true'</ff> - such metadata defined tells than category should be
   *    treated as main/default screen. If more than one categories has such metadata defined
   *    then it is assumed that first of them should be picked.
   * - <ff>YOUTUBE_SWIMLANE === 'true'</ff> - such metadata defined informs that this is a screen
   *    containing YouTube assets and this screen should be picked to display shared asset (this
   *    is for historical reason).
   */
  async getRootCategories () {
    const rootCategoryItems = await this.getAllCategoryItems(this.rootCategoryId)
    return rootCategoryItems.items
  }

  getCategoryItems (id = this.defaultCategoryId, sharedItem, page) {
    return this.getAllCategoryItems(id, sharedItem, page || 0)
  }

  extractGuid (link) {
    const guidRegex = /(\{){0,1}[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(\}){0,1}/
    return guidRegex.exec(link)[0]
  }

  getCategory (id, page) {
    return this.getCategoryLink(id, page || 0)
      .then((response) => {
        return Promise.resolve({ items: response.data.items, totalPages: response.data.totalPages })
      })
  }

  getAllCategoryItems (id = this.defaultCategoryId, sharedItem, page) {
    let totalPages = 1
    return this.getCategory(id, page)
      .then(response => {
        let promises = []
        totalPages = response.totalPages
        response.items.forEach(item => {
          if (item.type !== 'SEASON') {
            promises.push(this.getProgrammeDetailsByLinkWithMapping(item.link))
          }
        })
        return Promise.all(promises)
      })
      .then(items => {
        return Promise.resolve({ items: items, totalPages })
      })
  }

  getCategoryLink (id = this.defaultCategoryId, pageId = undefined) {
    if (pageId !== undefined) {
      return spinnaker.getVod().getCategoryPages(lang, id, pageId)
    } else {
      return spinnaker.getVod().getCategory(lang, id)
    }
  }

  /**
   * Returns Vod playback details for access token.
   * @param accessToken
   */
  getVodPlaybackDetailsForAccessToken () {
    return spinnaker.getEntitlements().getSharedPlaybackDetails(lang)
  }

  getVodPlaybackDetails (assetId) {
    let details = null
    return spinnaker.getEntitlements().getVoDPlaybackDetails(lang, assetId)
      .then((vodPlaybackDetailsResponse) => {
        details = vodPlaybackDetailsResponse.data
        return Promise.resolve(vodPlaybackDetailsResponse.data)
      })
      // .then(() => {
      //   return this.getEventIfOnBookmarks(assetId)
      // })
      // .then((response) => {
      //   details.lastPosition = response.data.lastPosition
      //   return Promise.resolve(details)
      // })
      .catch(() => {
        return Promise.resolve(details)
      })
  }

  refreshStream (streamId, contentToken) {
    return spinnaker.getEntitlements().refreshToken(lang, streamId, contentToken)
  }

  stopStream (streamId) {
    spinnaker.getEntitlements().deleteRefreshToken(lang, streamId)
  }

  uuidv4 () {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
      (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    )
  }

  switchToCasualUser () {
    storage.removeRefreshToken()
    spinnaker.getAuth().clearAuthData()
    // Reinstantiate keel-spin-moodule to avoid access token persist in header
    spinnaker = new Spinnaker(envSpin, navigator.userAgent + ' aftv-user')
    return this._casualSignUp()
  }

  async signInByUserCredential (username, password) {
    storage.removeRefreshToken()
    try {
      const response = await spinnaker.getAuth().signonUsernamePassword(username, password)
      storage.saveRefreshToken(response.data.refreshToken, REFRESH_TOKEN_TYPE_REGISTERED)
    } catch (err) {
      console.info(`Error during sign on: ${err}`)
      this.switchToCasualUser()
      throw err
    }
  }

  async _casualSignUp () {
    const self = this
    if (!navigator.onLine) {
      return false
    }
    return new Promise((resolve) => {
      // eslint-disable-next-line
      grecaptcha.ready(function () {
        // eslint-disable-next-line
        grecaptcha.execute(self.grecaptchaSiteKey, { action: 'homepage' }).then(function (captcha) {
          console.info(`_casualSignUp: grecaptcha token: ${captcha}`)
          spinnaker.getAuth().socialSignup({ captcha }).then((response) => {
            storage.saveRefreshToken(response.data.refreshToken, REFRESH_TOKEN_TYPE_CASUAL)
            console.info(`_casualSignUp: success`)
            return resolve(true)
          })
        })
      })
    })
  }

  appInstallId () {
    let appInstallId = storage.getAppInstallId()
    if (!appInstallId) {
      appInstallId = this.uuidv4()
      storage.saveAppInstallId(appInstallId)
    }
  }

  async _refreshToken (as) {
    const refreshToken = storage.getRefreshToken()

    if (!refreshToken) {
      console.info(`_refreshToken: No refresh token found - performing casual sign up`)
      return this._casualSignUp()
    }

    try {
      console.info(`_refreshToken: Refresh token found - attempting sign on`)
      const signOnResponse = await spinnaker.getAuth().signonRefresh(refreshToken, as)
      storage.saveRefreshToken(signOnResponse.data.refreshToken, storage.getRefreshTokenType())
      console.info(`_refreshToken: Sign on successful`)
    } catch (err) {
      if (err.response && err.response.status === 400) {
        console.error(`Got 400 error on sign-on by refresh token: ${err.response.data} - ` +
          `attempting casual sign up...`)
        storage.removeRefreshToken()
        return this._casualSignUp()
      }

      // don't remove refresh token - probably a network error or temporary server problem
      console.error(`Got status code ${err.response ? err.response.status : 0} when attempting to sign on`)
    }
  }

  async autoSignIn () {
    return this._refreshToken(true)
  }

  isAccountForRegisteredUser (accountSummary) {
    if (accountSummary && accountSummary.account && accountSummary.account.type) {
      return accountSummary.account.type !== 'Anonymous'
    } else {
      return Promise.resolve(false)
    }
  }

  isRegisteredUser () {
    return spinnaker.getAccount().summary().then((response) => {
      if (response &&
        response.data &&
        response.data.account) {
        return Promise.resolve(this.isAccountForRegisteredUser(response.data))
      } else {
        console.error('Failed to retrieve account summary.')
        return Promise.resolve(false)
      }
    })
  }

  async fetchAccountSummary () {
    await this._refreshToken(false)
    const response = await spinnaker.getAccount().summary()
    if (response &&
      response.data &&
      response.data.account) {
      return response.data
    } else {
      throw new Error('Invalid account summary response received')
    }
  }

  async deleteAccount () {
    await spinnaker.getAccount().delete()
  }

  fetchProfile () {
    return spinnaker.getAccount().profiles().then((profiles) => {
      for (let profile of profiles.data) {
        if (profile.isAccountOwner) {
          return profile
        }
      }
      return null
    })
  }

  updateProfile (profile) {
    return spinnaker.getProfiles().update(profile.id, profile)
  }

  async registerProfile (email, password, fullName, dateOfBirth, gender) {
    await this._refreshToken(false)
    await spinnaker.getAccount().register({
      username: email,
      email,
      password,
      fullName,
      dateOfBirth,
      gender
    })
  }

  registerPushNotifications (token) {
    console.info('Registering device for push notifications')
    return spinnaker.getPushNotifications().register(token).then((response) => {
      return Promise.resolve()
    }).catch((error) => {
      console.error(`error: ${error}`)
    })
  }

  passwordResetRequest (email) {
    const self = this
    return new Promise((resolve, reject) => {
      // eslint-disable-next-line
      grecaptcha.ready(function () {
        // eslint-disable-next-line
        grecaptcha.execute(self.grecaptchaSiteKey, { action: 'password_reset' }).then(function (captcha) {
          spinnaker.getAccount().passwordResetRequest(email, captcha).then((result) => {
            if (result.status === 204) {
              resolve(true)
            } else {
              reject(result)
            }
          }).catch((err) => {
            return reject(err)
          })
        })
      })
    })
  }

  async passwordResetPerform (token, newPassword) {
    await spinnaker.getAccount().passwordResetPerform(token, newPassword)
  }

  /**
   * Returns recommend link
   * @param progId
   */
  getRecommendLink (progId) {
    return spinnaker.getContentSharing().recommend(progId, 'AFTV')
  }

  /**
   * Returns stats for the poll e.g. number of votes per answer or total votes
   * @param flexItemId
   */
  async getPollStats (flexItemId) {
    await this._refreshToken(false)
    return spinnaker.getPolls().getStats(flexItemId)
  }

  /**
   * User post a vote in poll
   * @param flexItemId
   * @param option
   */
  async addVote (flexItemId, option) {
    await this._refreshToken(false)
    return spinnaker.getPolls().insert(flexItemId, { selectedOption: option })
  }

  /**
   * Get details of the poll for user
   * @param flexItemId
   */
  async getMyPoll (flexItemId) {
    await this._refreshToken(false)
    return spinnaker.getPolls().getMyPolls(flexItemId)
  }

  /**
   * Adds event to bookmarks system, once/v1/stat playout happens
   * @param {Object} event event information
   * @param {Number} lastPosition last position in ms
   */
  addEventToBookmarks (progId, lastPosition) {
    return this.spinnaker.getMyContent()
      .bookmarksProgrammePut(progId, lastPosition)
  }

  /**
   * removes event from bookmarks
   * @param {Object} event
   */
  removeEventFromBookmarks (progId) {
    return this.spinnaker.getMyContent()
      .bookmarksProgrammeDel(progId)
  }

  postVideoEvent (event) {
    this.spinnaker.getClientVideoEvents().postEvent(event)
  }

  stopPlayout (source, currentTime) {
    // const { progId } = source
    // this.addEventToBookmarks(progId, currentTime)
  }

  async startStream (playoutContent, shareToken) {
    let streamDetails = playoutContent
    if (playoutContent.type === 'video/youtube') {
      streamDetails.streamId = new SharedFunctions().uuid()
      console.info(`Synthesized stream id ${streamDetails.streamId} for YouTube asset`)
    } else if (playoutContent.useRedeem) {
      streamDetails = await this.redeem(shareToken)
      console.info(`Got stream details from redeem successfully`)
    } else {
      streamDetails = await this.getVodPlaybackDetails(playoutContent.progId)
      console.info(`Got stream details from redeem successfully`)
    }
    return streamDetails
  }

  /**
   * Returns Open Graph for provided Base64 encoded image.
   * @param base64Image
   * @returns {Promise<string>}
   */
  async getOpenGraphForBase64Image (base64Image) {
    return new Promise((resolve, reject) => {
      spinnaker.getContentSharing().getOpenGraphForBase64Image(base64Image).then((response) => {
        if (response.status === 200 && response.data) {
          resolve(response.data)
        } else {
          reject(response)
        }
      }).catch((error) => {
        return reject(error)
      })
    })
  }
}

const spinnakerApi = new SpinnakerApi()
export { spinnakerApi }
